import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dropdown", "badge", "list", "loading", "empty"]
  static values = {
    url: String
  }

  connect() {
    this.isOpen = false
    // Close dropdown when clicking outside
    this.boundClickOutside = this.clickOutside.bind(this)
    // Close dropdown when pressing ESC
    this.boundEscapeKey = this.escapeKey.bind(this)
  }

  disconnect() {
    this.removeEventListeners()
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  async open() {
    this.isOpen = true
    this.dropdownTarget.classList.remove('hidden')

    // Add event listeners
    document.addEventListener('click', this.boundClickOutside)
    document.addEventListener('keydown', this.boundEscapeKey)

    // Load notifications
    await this.loadNotifications()
  }

  close() {
    this.isOpen = false
    this.dropdownTarget.classList.add('hidden')
    this.removeEventListeners()
  }

  removeEventListeners() {
    document.removeEventListener('click', this.boundClickOutside)
    document.removeEventListener('keydown', this.boundEscapeKey)
  }

  clickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  escapeKey(event) {
    if (event.key === 'Escape') {
      this.close()
    }
  }

  async loadNotifications() {
    // Show loading state
    this.loadingTarget.classList.remove('hidden')
    this.listTarget.innerHTML = ''
    this.emptyTarget.classList.add('hidden')

    try {
      const response = await fetch(this.urlValue)
      const data = await response.json()

      this.loadingTarget.classList.add('hidden')

      if (data.notifications.length === 0) {
        this.emptyTarget.classList.remove('hidden')
      } else {
        this.renderNotifications(data.notifications)
      }

      // Update badge count
      this.updateBadgeCount(data.unread_count)
    } catch (error) {
      console.error('Failed to load notifications:', error)
      this.loadingTarget.classList.add('hidden')
      this.listTarget.innerHTML = '<div class="p-4 text-center text-red-600 text-sm">Erro ao carregar notificações</div>'
    }
  }

  renderNotifications(notifications) {
    this.listTarget.innerHTML = notifications.map(notification => {
      const colorClass = this.getColorClass(notification.notification_type)
      const iconSvg = this.getIconSvg(notification.notification_type)
      const bgClass = notification.read ? 'bg-white hover:bg-gray-50' : 'bg-blue-50 hover:bg-blue-100'

      return `
        <a href="${notification.url}"
           data-notification-id="${notification.id}"
           data-action="click->notification-dropdown#markAsRead"
           class="${bgClass} block px-4 py-3 border-b border-gray-100 transition-colors">
          <div class="flex items-start gap-3">
            <div class="flex-shrink-0 mt-1">
              <div class="w-8 h-8 rounded-full ${colorClass} flex items-center justify-center">
                ${iconSvg}
              </div>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-semibold text-gray-900 mb-1">${notification.title}</p>
              <p class="text-xs text-gray-600 line-clamp-2 mb-1">${notification.message}</p>
              <p class="text-xs text-gray-500">${notification.created_at}</p>
            </div>
            ${!notification.read ? '<div class="flex-shrink-0"><span class="inline-block w-2 h-2 bg-blue-600 rounded-full"></span></div>' : ''}
          </div>
        </a>
      `
    }).join('')
  }

  async markAsRead(event) {
    const notificationId = event.currentTarget.dataset.notificationId

    try {
      const response = await fetch(`/admin/notifications/${notificationId}/mark_as_read`, {
        method: 'PATCH',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      })

      const data = await response.json()
      if (data.success) {
        this.updateBadgeCount(data.unread_count)
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error)
    }
  }

  async markAllAsRead(event) {
    event.preventDefault()
    event.stopPropagation()

    try {
      const response = await fetch('/admin/notifications/mark_all_as_read', {
        method: 'PATCH',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      })

      const data = await response.json()
      if (data.success) {
        this.updateBadgeCount(0)
        // Reload notifications to update UI
        await this.loadNotifications()
      }
    } catch (error) {
      console.error('Failed to mark all as read:', error)
    }
  }

  updateBadgeCount(count) {
    const badges = document.querySelectorAll('[data-notification-badge]')
    badges.forEach(badge => {
      if (count > 0) {
        badge.textContent = count
        badge.style.display = 'inline-flex'
      } else {
        badge.style.display = 'none'
      }
    })
  }

  getColorClass(type) {
    switch(type) {
      case 'direct_sale_approved':
        return 'bg-green-100 text-green-600'
      case 'direct_sale_rejected':
      case 'follow_up_overdue':
      case 'pix_expired':
        return 'bg-red-100 text-red-600'
      case 'direct_sale_approval_needed':
      case 'approval_request':
      case 'pix_expiring_soon':
        return 'bg-yellow-100 text-yellow-600'
      case 'payment_link_ready':
        return 'bg-orange-100 text-orange-600'
      case 'follow_up_reminder':
        return 'bg-purple-100 text-purple-600'
      case 'contract_ready':
        return 'bg-blue-100 text-blue-600'
      default:
        return 'bg-gray-100 text-gray-600'
    }
  }

  getIconSvg(type) {
    const baseClass = 'w-4 h-4'

    switch(type) {
      case 'direct_sale_approved':
        return `<svg class="${baseClass}" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg>`
      case 'direct_sale_rejected':
        return `<svg class="${baseClass}" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>`
      case 'direct_sale_approval_needed':
      case 'approval_request':
        return `<svg class="${baseClass}" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>`
      case 'payment_link_ready':
        return `<svg class="${baseClass}" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"></path></svg>`
      case 'contract_ready':
        return `<svg class="${baseClass}" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>`
      case 'follow_up_reminder':
      case 'follow_up_overdue':
        return `<svg class="${baseClass}" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>`
      case 'pix_expiring_soon':
      case 'pix_expired':
        return `<svg class="${baseClass}" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>`
      default:
        return `<svg class="${baseClass}" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"></path></svg>`
    }
  }
}
